import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';

/**
 * Confirmation dialog for subscription actions (upgrade / downgrade /
 * billing-cycle switch / cancel).
 *
 * Every subscription action is destructive or money-related, so each one must
 * be confirmed explicitly and the dialog must explain in detail what will
 * happen — in particular whether a new payment is charged immediately, or
 * whether the change is only scheduled for the end of the billing period.
 *
 * Props:
 * - title:        short question, e.g. "Upgrade to Intermediate?"
 * - summary:      one-line answer, leads with the payment consequence
 * - children:     detailed bullet list (what changes, when, what it costs)
 * - confirmLabel: action verb on the confirm button
 * - onConfirm:    called when the user confirms; receives nothing
 * - isBusy:       shows a spinner and disables the buttons while the action runs
 * - destructive:  renders the confirm button in the danger (rose) style
 */
export default function ConfirmSubscriptionAction({
  open,
  onOpenChange,
  title,
  summary,
  children,
  confirmLabel,
  cancelLabel = 'Go back',
  onConfirm,
  isBusy = false,
  destructive = false,
  confirmTestId,
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="subscription-action-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{summary}</AlertDialogDescription>
        </AlertDialogHeader>
        {children && (
          <div className="text-sm text-slate-600 space-y-2 py-1">{children}</div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isBusy}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // Keep the dialog open while the action runs (isBusy renders the
              // spinner) instead of Radix's default close-on-click.
              e.preventDefault();
              onConfirm();
            }}
            disabled={isBusy}
            className={
              destructive
                ? 'bg-rose-600 hover:bg-rose-700 text-white'
                : 'bg-slate-900 hover:bg-slate-800 text-white'
            }
            data-testid={confirmTestId}
          >
            {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
